import { useState, useCallback } from 'react';
import { Product } from '@/mocks/products';

export interface CartItem {
  product: Product;
  quantity: number;
}

let cartState: CartItem[] = [];
const listeners: Array<() => void> = [];

function notifyListeners() {
  listeners.forEach((fn) => fn());
}

export function useCart() {
  const [, forceUpdate] = useState(0);

  const subscribe = useCallback(() => {
    const update = () => forceUpdate((n) => n + 1);
    listeners.push(update);
    return () => {
      const idx = listeners.indexOf(update);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  // Subscribe on mount
  useState(() => {
    const unsub = subscribe();
    return unsub;
  });

  const addToCart = useCallback((product: Product, quantity = 1) => {
    const existing = cartState.find((item) => item.product.id === product.id);
    if (existing) {
      cartState = cartState.map((item) =>
        item.product.id === product.id
          ? { ...item, quantity: item.quantity + quantity }
          : item
      );
    } else {
      cartState = [...cartState, { product, quantity }];
    }
    notifyListeners();
  }, []);

  const removeFromCart = useCallback((productId: number) => {
    cartState = cartState.filter((item) => item.product.id !== productId);
    notifyListeners();
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    if (quantity <= 0) {
      cartState = cartState.filter((item) => item.product.id !== productId);
    } else {
      cartState = cartState.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      );
    }
    notifyListeners();
  }, []);

  const clearCart = useCallback(() => {
    cartState = [];
    notifyListeners();
  }, []);

  const totalItems = cartState.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cartState.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0
  );

  return {
    cartItems: cartState,
    totalItems,
    totalPrice,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
  };
}
